import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Trophy, Lock } from 'lucide-react';

const Earned = ({ row }) => (
  <div className="rounded-xl border border-amber-300/30 bg-amber-300/[0.07] px-4 py-3"
       data-testid={`milestone-${row.key}`}>
    <div className="flex items-start justify-between gap-3">
      <span className="flex items-start gap-2.5 min-w-0">
        <Trophy className="w-4 h-4 text-amber-200 shrink-0 mt-0.5" />
        <span className="min-w-0">
          <span className="block text-sm text-white">{row.title}</span>
          <span className="block text-xs text-white/50 mt-0.5">{row.note}</span>
        </span>
      </span>
      {row.earned_at && (
        <span className="text-[11px] text-amber-200/60 shrink-0">
          {new Date(row.earned_at).toLocaleDateString()}
        </span>
      )}
    </div>
  </div>
);

const Next = ({ row }) => {
  const pct = Math.min(100, Math.round((row.count / row.target) * 100));
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3"
         data-testid={`milestone-next-${row.key}`}>
      <div className="flex items-start justify-between gap-3">
        <span className="flex items-start gap-2.5 min-w-0">
          <Lock className="w-3.5 h-3.5 text-white/30 shrink-0 mt-1" />
          <span className="min-w-0">
            <span className="block text-sm text-white/70">{row.title}</span>
            <span className="block text-xs text-white/40 mt-0.5">{row.note}</span>
          </span>
        </span>
        <span className="text-[11px] text-white/40 shrink-0">{row.count} of {row.target}</span>
      </div>
      <div className="mt-3 h-1 rounded-full bg-white/[0.06] overflow-hidden">
        <div className="h-full rounded-full bg-white/30 transition-all duration-700"
             style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

/** Theirs alone — nobody else is shown this page. */
export const Milestones = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/companion/milestones`)
      .then((r) => setData(r.data))
      .catch(() => setData({ groups: [], earned_count: 0, total_count: 0 }));
  }, []);

  if (!data) {
    return <p className="text-sm text-white/50" data-testid="milestones-loading">Counting the ground you have covered…</p>;
  }

  return (
    <div className="space-y-10" data-testid="milestones">
      <header className="space-y-2">
        <p className="text-2xl font-semibold text-white" data-testid="milestones-count">
          {data.earned_count} of {data.total_count} reached
        </p>
        <p className="text-sm text-white/45">
          These are yours alone — no practitioner and no admin sees this page.
        </p>
      </header>

      {data.groups.map((group) => (
        <section className="space-y-3" key={group.category}
                 data-testid={`milestone-group-${group.category.replace(/\s+/g, '-')}`}>
          <h2 className="text-base md:text-lg font-semibold text-white">
            <span className="capitalize">{group.category}</span>
            <span className="text-sm font-normal text-white/35">
              {' '}· {group.earned.length} of {group.total}
            </span>
          </h2>
          <div className="space-y-2">
            {group.earned.map((row) => <Earned key={row.key} row={row} />)}
            {group.next && <Next row={group.next} />}
            {!group.earned.length && !group.next && (
              <p className="text-sm text-white/40">All of these are behind you.</p>
            )}
          </div>
        </section>
      ))}
    </div>
  );
};

export default Milestones;
